import React from 'react';
import './NotificationsPanel.css';
import { useAuth } from '../contexts/authContext';
import supabase from '../supabaseClient';
import Toast from '../pages/Academy/Academylms/common/Toast';
import { useNavigate } from 'react-router-dom';

const NotificationsPanel = ({
  isOpen,
  onClose,
  notifications = [], // external notifications (optional)
  onMarkAllRead,
  onToggleRead,
  onDelete,
  onDeleteAll
}) => {
  const { user } = useAuth(); // <-- Use the auth context
  const navigate = useNavigate();
  // Helper: get user's LMS roles (if any)
  const userLmsRoles = Array.isArray(user?.lms_roles) ? user.lms_roles : [];
  // Notification type tabs
  const notificationTypes = [
    { key: 'all', label: 'All' },
    { key: 'system', label: 'System' },
    { key: 'assignment', label: 'Assignment' },
    { key: 'event', label: 'Event' },
    { key: 'message', label: 'Message' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'badge', label: 'Badge' },
    { key: 'role', label: 'Role' },
    { key: 'admin', label: 'Admin' },
    { key: 'birthday', label: 'Birthday' },
    { key: 'custom', label: 'Custom' },
  ];
  const [activeTab, setActiveTab] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [showSettings, setShowSettings] = React.useState(false);

  // Notification settings and preferences state
  const [settings, setSettings] = React.useState({}); // { type: enabled }
  const [preferences, setPreferences] = React.useState({
    delivery_realtime: true,
    delivery_email: false,
    delivery_push: false,
  });
  const [settingsLoading, setSettingsLoading] = React.useState(false);
  const [settingsError, setSettingsError] = React.useState(null);

  // --- DB-backed notifications (fallback) ---
  const [dbNotifications, setDbNotifications] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const useDb = notifications.length === 0;

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ''; }
  };

  const mapRow = (row) => ({
    id: row.id,
    user_id: row.user_id,
    from_user_id: row.from_user_id,
    type: ['connection_birthday','birthday_today'].includes(row.type) ? 'birthday' : (row.type || 'custom'),
    raw_type: row.type,
    title: row.title || (row.type === 'connection_birthday' ? '🎂 Birthday today' : row.type === 'birthday_today' ? '🎉 Happy Birthday!' : 'Notification'),
    content: row.content || (row.type === 'connection_birthday' ? `${row?.data?.display_name || 'Your connection'} has a birthday today.` : ''),
    data: row.data || {},
    read: !!row.is_read,
    time: formatTime(row.created_at),
    created_at: row.created_at,
  });

  const fetchDbNotifications = async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (Array.isArray(data)) setDbNotifications(data.map(mapRow));
    setLoading(false);
  };

  React.useEffect(() => {
    if (!useDb || !user?.id) return;
    fetchDbNotifications();
    const channel = supabase
      .channel('notifications-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, (payload) => {
        const row = payload.new;
        setDbNotifications(prev => [mapRow(row), ...prev]);
        if (preferences?.delivery_realtime !== false) {
          const t = row.title || (row.type === 'connection_birthday' ? '🎂 Birthday today' : 'New notification');
          showToast(t, 'success');
        }
      })
      .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch {} };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDb, user?.id]);

  const effectiveNotifications = useDb ? dbNotifications : notifications;

  // Toast state
  const [toast, setToast] = React.useState({ open: false, message: '', type: 'info' });
  const showToast = (message, type = 'info', duration = 3500) => {
    setToast({ open: true, message, type, duration });
  };
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  // Load settings and preferences from Supabase
  React.useEffect(() => {
    if (!showSettings || !user?.id) return;
    let cancelled = false;
    async function fetchSettings() {
      setSettingsLoading(true);
      setSettingsError(null);
      try {
        // Fetch notification_settings
        const { data: settingsRows, error: settingsErr } = await supabase
          .from('notification_settings')
          .select('notification_type, enabled')
          .eq('profile_id', user.id);
        if (settingsErr) throw settingsErr;
        const settingsMap = {};
        if (settingsRows) {
          for (const row of settingsRows) {
            settingsMap[row.notification_type] = row.enabled;
          }
        }
        // Fetch notification_preferences
        const { data: prefRows, error: prefErr } = await supabase
          .from('notification_preferences')
          .select('delivery_realtime, delivery_email, delivery_push')
          .eq('profile_id', user.id)
          .maybeSingle();
        if (prefErr) throw prefErr;
        if (!cancelled) {
          if (prefRows) {
            setPreferences(prefRows);
          } else {
            // Insert default row if missing
            await supabase.from('notification_preferences').insert({
              profile_id: user.id,
              delivery_realtime: true,
              delivery_email: false,
              delivery_push: false,
              updated_at: new Date().toISOString(),
            });
            setPreferences({ delivery_realtime: true, delivery_email: false, delivery_push: false });
          }
        }
      } catch (err) {
        if (!cancelled) setSettingsError(err.message || 'Failed to load notification settings');
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    }
    fetchSettings();
    return () => { cancelled = true; };
  }, [showSettings, user?.id]);
  const [selectedNotification, setSelectedNotification] = React.useState(null);
  const [tabScrollPosition, setTabScrollPosition] = React.useState(0);
  const tabsContainerRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  // Check scroll state
  const checkScrollState = React.useCallback(() => {
    if (tabsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;
      const maxScrollLeft = scrollWidth - clientWidth;
      
      // More aggressive overflow detection
      const hasOverflow = scrollWidth > clientWidth + 1;
      
      if (hasOverflow) {
        setCanScrollLeft(scrollLeft > 1);
        setCanScrollRight(scrollLeft < maxScrollLeft - 1);
        
        // If at the start and there's overflow, enable right arrow
        if (scrollLeft === 0) {
          setCanScrollRight(true);
        }
      } else {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
    }
  }, []);

  // Scroll tabs left/right
  const scrollTabs = (direction) => {
    if (tabsContainerRef.current) {
      const scrollAmount = 120;
      const newScrollLeft = tabsContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      tabsContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  // Handle wheel scroll on tabs (imperatively, not via React)
  const handleTabsWheel = React.useCallback((e) => {
    
    if (tabsContainerRef.current) {
      const scrollAmount = e.deltaY > 0 ? 60 : -60;
      tabsContainerRef.current.scrollLeft += scrollAmount;
      e.preventDefault(); // Now allowed because listener is passive: false
    }
  }, []);

  // Attach wheel event imperatively with passive: false
  // Attach wheel event on mouse enter, remove on mouse leave
  const handleTabsMouseEnter = () => {
    const container = tabsContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleTabsWheel, { passive: false });
      
    }
  };
  const handleTabsMouseLeave = () => {
    const container = tabsContainerRef.current;
    if (container) {
      container.removeEventListener('wheel', handleTabsWheel);
      
    }
  };


  // Update scroll state on scroll and resize
  React.useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScrollState);

    // Use ResizeObserver for dynamic content
    let resizeObs = new window.ResizeObserver(() => {
      checkScrollState();
    });
    resizeObs.observe(container);

    // Debug: log container and children info after refresh
    requestAnimationFrame(() => {
      if (container) {
        console.debug('[NotificationsPanel] Tabs container:', {
          scrollWidth: container.scrollWidth,
          clientWidth: container.clientWidth,
          childCount: container.children.length
        });
        checkScrollState();
      }
    });
    setTimeout(checkScrollState, 50);
    setTimeout(checkScrollState, 200);
    setTimeout(checkScrollState, 500);

    return () => {
      container.removeEventListener('scroll', checkScrollState);
      container.removeEventListener('wheel', handleTabsWheel);
      if (resizeObs) resizeObs.disconnect();
    };
  }, [checkScrollState, handleTabsWheel, notificationTypes.length, activeTab, notifications.length]);

  // Check scroll state on resize
  React.useEffect(() => {
    const handleResize = () => checkScrollState();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkScrollState]);


  // Count unread notifications for badge
  const unreadCount = effectiveNotifications.filter(n => !n.read && isNotificationVisible(n)).length;

  // Filtering logic: user sees notification if...
  const isNotificationVisible = (n) => {
    if (n.target === 'general') return true;
    if (n.target === 'lms' && userLmsRoles.length > 0) return true;
    if (n.user_id && n.user_id === user?.id) return true;
    if (Array.isArray(n.roles) && n.roles.some(role => userLmsRoles.includes(role))) return true;
    return false;
  };
  // Apply all filters
  let displayNotifications = effectiveNotifications.filter(isNotificationVisible);
  if (activeTab !== 'all') displayNotifications = displayNotifications.filter(n => n.type === activeTab);
  if (search) {
    const s = search.toLowerCase();
    displayNotifications = displayNotifications.filter(n =>
      (n.title && n.title.toLowerCase().includes(s)) ||
      (n.content && n.content.toLowerCase().includes(s)) ||
      (n.type && n.type.toLowerCase().includes(s))
    );
  }

  // Get icon for notification tabs
  const getTabIcon = (type) => {
    switch (type) {
      case 'all': return '🔔';
      case 'system': return '⚙️';
      case 'assignment': return '📝';
      case 'event': return '📅';
      case 'message': return '💬';
      case 'feedback': return '💭';
      case 'badge': return '🏆';
      case 'role': return '👤';
      case 'admin': return '🔧';
      case 'custom': return '⭐';
      default: return '🔔';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'birthday':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-8a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v8" />
            <path d="M4 21h16" />
            <path d="M12 7V3" />
            <path d="M12 3c1.5 1.5 0 3 0 3s-1.5-1.5 0-3z" />
          </svg>
        );
      case 'message':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        );
      case 'reminder':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        );
      case 'system':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
        );
    }
  };

  const panelRef = React.useRef(null);
  // Unread count for current tab

  React.useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose?.();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Default handlers (DB-backed) if none provided by parent
  const toggleRead = async (n) => {
    if (onToggleRead) return onToggleRead(n);
    if (!useDb) return;
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: !n.read })
      .eq('id', n.id);
    if (!error) setDbNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: !n.read } : x));
  };

  const markAllRead = async () => {
    if (onMarkAllRead) return onMarkAllRead();
    if (!useDb || !user?.id) return;
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    if (!error) setDbNotifications(prev => prev.map(x => ({ ...x, read: true })));
  };

  const deleteOne = async (n) => {
    if (onDelete) return onDelete(n);
    if (!useDb) return;
    const confirmed = window.confirm('Delete this notification?');
    if (!confirmed) return;
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', n.id);
    if (!error) setDbNotifications(prev => prev.filter(x => x.id !== n.id));
  };

  const deleteAll = async () => {
    if (onDeleteAll) return onDeleteAll();
    if (!useDb || !user?.id) return;
    const confirmed = window.confirm('Delete all notifications? This action cannot be undone.');
    if (!confirmed) return;
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', user.id);
    if (!error) setDbNotifications([]);
  };

  if (!isOpen) return null;

  return (
    <div className="notifications-panel" ref={panelRef}>
      <Toast
        open={toast.open}
        message={toast.message}
        type={toast.type}
        onClose={closeToast}
        duration={toast.duration || 3500}
      />
      <button className="notification-settings-btn top-left" aria-label="Notification settings" onClick={() => setShowSettings(!showSettings)}>
        <span>⚙</span>
      </button>
      <button className="close-button" aria-label="Close notifications" onClick={onClose}><span>×</span></button>
      <div className="panel-title-row">
          <h3 className="notification-panel-title">Notifications</h3>
        </div>
        <div className="notification-tabs-row">
          <button 
            className={`tab-scroll-arrow left ${!canScrollLeft ? 'disabled' : ''}`}
            onClick={() => scrollTabs('left')}
            disabled={!canScrollLeft}
            aria-label="Scroll tabs left"
          >
            ‹
          </button>
          <div 
            className="notification-tabs"
            ref={tabsContainerRef}
            tabIndex={0}
            onMouseEnter={e => { handleTabsMouseEnter(); checkScrollState(e); }}
            onMouseLeave={handleTabsMouseLeave}
            onMouseMove={checkScrollState}
          >
            {notificationTypes.map(t => (
              <button
                key={t.key}
                className={`notification-tab${activeTab===t.key?' active':''}`}
                onClick={()=>setActiveTab(t.key)}
              >
                <span className="tab-icon">{getTabIcon(t.key)}</span>
                {t.label}
                {t.key==='all' && unreadCount>0 && <span className="notification-badge-glow">{unreadCount}</span>}
                {t.key!=='all' && activeTab===t.key && unreadCount>0 && <span className="notification-badge-glow">{unreadCount}</span>}
              </button>
            ))}
          </div>
          <button 
            className={`tab-scroll-arrow right ${!canScrollRight ? 'disabled' : ''}`}
            onClick={() => scrollTabs('right')}
            disabled={!canScrollRight}
            aria-label="Scroll tabs right"
          >
            ›
          </button>

        </div>
        <div className="notification-search-bar">
          <input
            type="text"
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder="Search notifications..."
            aria-label="Search notifications"
          />
          {search && (
            <button
              aria-label="Clear search"
              className="notification-clear-search"
              onClick={()=>setSearch('')}
            >✕</button>
          )}
        </div>
        {showSettings && (
          <div className="notification-settings-panel">
            <h4>Notification Settings</h4>
            <div className="settings-section">
              <h5>Notification Types</h5>
              {notificationTypes.filter(t => t.key !== 'all').map(type => (
                <label key={type.key} className="setting-checkbox">
                  <input
                    type="checkbox"
                    checked={settings[type.key] ?? true}
                    onChange={async (e) => {
                      const enabled = e.target.checked;
                      setSettings((prev) => ({ ...prev, [type.key]: enabled }));
                      try {
                        await supabase.from('notification_settings').upsert({
                          profile_id: user.id,
                          notification_type: type.key,
                          enabled,
                          updated_at: new Date().toISOString(),
                        }, { onConflict: ['profile_id', 'notification_type'] });
                        showToast('Notification setting saved!', 'success');
                      } catch (err) {
                        showToast('Failed to save notification setting.', 'error');
                      }
                    }}
                  />
                  <span className="tab-icon">{getTabIcon(type.key)}</span>
                  {type.label}
                </label>
              ))}
            </div>
            <div className="settings-section">
  <h5>Delivery Preferences</h5>
  <label className="setting-checkbox">
    <input
      type="checkbox"
      checked={preferences.delivery_realtime ?? true}
      onChange={async (e) => {
        const delivery_realtime = e.target.checked;
        setPreferences((prev) => ({ ...prev, delivery_realtime }));
        try {
          await supabase.from('notification_preferences').upsert({
            profile_id: user.id,
            delivery_realtime,
            delivery_email: preferences.delivery_email,
            delivery_push: preferences.delivery_push,
            updated_at: new Date().toISOString(),
          }, { onConflict: ['profile_id'] });
          showToast('Real-time notifications preference saved!', 'success');
        } catch (err) {
          showToast('Failed to save real-time notifications preference.', 'error');
        }
      }}
    />
    Real-time (in-app) notifications
  </label>
  <label className="setting-checkbox">
    <input
      type="checkbox"
      checked={preferences.delivery_email ?? false}
      onChange={async (e) => {
        const delivery_email = e.target.checked;
        setPreferences((prev) => ({ ...prev, delivery_email }));
        try {
          await supabase.from('notification_preferences').upsert({
            profile_id: user.id,
            delivery_realtime: preferences.delivery_realtime,
            delivery_email,
            delivery_push: preferences.delivery_push,
            updated_at: new Date().toISOString(),
          }, { onConflict: ['profile_id'] });
          showToast('Email digest preference saved!', 'success');
        } catch (err) {
          showToast('Failed to save email digest preference.', 'error');
        }
      }}
    />
    Email digest
  </label>
  <label className="setting-checkbox">
    <input
      type="checkbox"
      checked={preferences.delivery_push ?? false}
      onChange={async (e) => {
        const delivery_push = e.target.checked;
        setPreferences((prev) => ({ ...prev, delivery_push }));
        try {
          await supabase.from('notification_preferences').upsert({
            profile_id: user.id,
            delivery_realtime: preferences.delivery_realtime,
            delivery_email: preferences.delivery_email,
            delivery_push,
            updated_at: new Date().toISOString(),
          }, { onConflict: ['profile_id'] });
          showToast('Push notifications preference saved!', 'success');
        } catch (err) {
          showToast('Failed to save push notifications preference.', 'error');
        }
      }}
    />
    Push notifications
  </label>
</div>
            <div className="settings-actions">
              <button className="settings-btn export">Export Settings</button>
              <button className="settings-btn import">Import Settings</button>
              <button className="close-button" aria-label="Close notifications" onClick={onClose}><span>×</span></button>
            </div>
          </div>
        )}

        {/* Notification List or Empty State */}
        {!showSettings && (
          <div className="notifications-list">
            {loading ? (
            <div className="no-notifications">Loading…</div>
          ) : dbNotifications.length === 0 ? (
            <div className="no-notifications">No notifications found.</div>
          ) : (
            dbNotifications.map((n, i) => (
              <div key={n.id || i} className={`notification-item${n.read ? '' : ' unread'}`}>
                <div className={`notification-icon ${n.type}`}>{getNotificationIcon(n.type)}</div>
                <div className="notification-content">
                  <div className="notification-title">{n.title}</div>
                  <div className="notification-text">
                    {n.type === 'birthday' && n.data?.avatar_url && (
                      <img src={n.data.avatar_url} alt="avatar" style={{ width: 24, height: 24, borderRadius: '50%', marginRight: 8, verticalAlign: 'middle' }} />
                    )}
                    {n.content}
                    {n.type === 'birthday' && n.data?.profile_id && (
                      <button
                        className="send-wishes-btn"
                        style={{ marginLeft: 8 }}
                        onClick={() => navigate(`/app/profile/${n.data.profile_id}`)}
                      >
                        Send wishes
                      </button>
                    )}
                  </div>
                  <div className="notification-time">{n.time}</div>
                </div>
                <button className="toggle-read-btn" onClick={() => toggleRead(n)}>
                  {n.read ? 'Mark as unread' : 'Mark as read'}
                </button>
                <button className="delete-notification-btn" onClick={() => deleteOne(n)}>
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      )}

        {/* Upcoming Birthdays (only on Birthday tab) */}
        {!showSettings && activeTab === 'birthday' && (
          <>
            <UpcomingBirthdays />
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 10 }}>
              <button
                className="view-all"
                onClick={() => navigate('/app/birthdays')}
                aria-label="See all birthdays"
              >
                See all
              </button>
            </div>
          </>
        )}

        {/* Footer */}
        {!showSettings && (
          <div className="notifications-footer">
            <button className="mark-all-read" onClick={markAllRead}>Mark all as read</button>
            <button className="view-all notifications-delete-all" onClick={deleteAll}>Delete all</button>
          </div>
        )}
      </div>
  );
}

// --- Upcoming Birthdays Subcomponent ---
function UpcomingBirthdays() {
  const { user } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const daysUntilNext = (dob) => {
    const d = new Date(dob);
    const now = new Date();
    const next = new Date(Date.UTC(now.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    let delta = Math.round((next.getTime() - todayUTC) / 86400000);
    if (delta < 0) {
      const nextYear = new Date(Date.UTC(now.getUTCFullYear() + 1, d.getUTCMonth(), d.getUTCDate()));
      delta = Math.round((nextYear.getTime() - todayUTC) / 86400000);
    }
    return delta;
  };

  const loadUpcoming = React.useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    // get connections both ways accepted
    const { data: out } = await supabase
      .from('connections')
      .select('connection_id')
      .eq('user_id', user.id)
      .eq('status', 'accepted');
    const { data: inc } = await supabase
      .from('connections')
      .select('user_id')
      .eq('connection_id', user.id)
      .eq('status', 'accepted');
    const ids = new Set();
    (out || []).forEach(r => ids.add(r.connection_id));
    (inc || []).forEach(r => ids.add(r.user_id));
    if (ids.size === 0) { setItems([]); setLoading(false); return; }
    const allIds = Array.from(ids);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, name, avatar_url, date_of_birth, show_birthday')
      .in('id', allIds);
    const upcoming = (profiles || [])
      .filter(p => p.show_birthday !== false && p.date_of_birth)
      .map(p => ({
        id: p.id,
        display_name: p.display_name || p.name || 'User',
        avatar_url: p.avatar_url,
        date_of_birth: p.date_of_birth,
        days: daysUntilNext(p.date_of_birth)
      }))
      .filter(x => x.days > 0 && x.days <= 7)
      .sort((a, b) => a.days - b.days)
      .slice(0, 12);
    setItems(upcoming);
    setLoading(false);
  }, [user?.id]);

  React.useEffect(() => { loadUpcoming(); }, [loadUpcoming]);

  if (loading) return <div className="notifications-list"><div className="no-notifications">Loading upcoming birthdays…</div></div>;
  if (items.length === 0) return null;
  return (
    <div className="upcoming-birthdays">
      <h4 style={{ marginTop: 16 }}>Upcoming birthdays</h4>
      <div className="upcoming-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
        {items.map(it => (
          <a key={it.id} className="upcoming-card" href={`/app/profile/${it.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <img src={it.avatar_url || '/default-avatar.png'} alt={it.display_name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong>{it.display_name}</strong>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{it.days === 1 ? 'Tomorrow' : `in ${it.days} days`}</span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default NotificationsPanel;
